import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { ArrowRight, CheckCircle, Warning, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import MergeReviewPage from "./MergeReviewPage";
import CitationsPage from "./CitationsPage";
import QualityPage from "./QualityPage";
import ValidationPage from "./ValidationPage";
import ReleasePage from "./ReleasePage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  ["overview", "Overview"], ["duplicates", "Duplicates"], ["sources", "Source Verification"],
  ["conflicts", "Conflicts"], ["validation", "Validation"], ["release", "Approval & Release"],
];
const SEVERITY = { critical: "bg-red-600", high: "bg-orange-500", medium: "bg-amber-500" };

const Overview = ({ productId, goTo }) => {
  const [d, setD] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const load = () => axios.get(`${API}/evidence-experience/${productId}/review-queue`).then((r) => setD(r.data)).catch(console.error);
  useEffect(() => { setD(null); load(); }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/inventory/${productId}/verification/refresh`);
      toast.success(`Verification recomputed — ${r.data.verified ?? 0} of ${r.data.total ?? 0} assets meet the verified bar`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };
  if (!d) return <p className="px-4 py-10 text-center text-xs text-slate-400">Loading…</p>;
  const h = d.health;
  return (
    <div className="space-y-6" data-testid="review-overview">
      <div className="bg-white border border-slate-200 p-5" data-testid="evidence-health">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading font-bold text-sm">Evidence Health</p>
          <button data-testid="refresh-verification-btn" disabled={refreshing} onClick={refresh}
            className="text-[11px] font-semibold px-3 py-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5">
            {refreshing && <CircleNotch size={11} className="animate-spin" />}
            {refreshing ? "Recomputing…" : "Refresh verification"}
          </button>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {[["total", "evidence assets"], ["processed", "processed"], ["verified", "verified"],
            ["needs_review", "requiring review"], ["citation_issues", "citation issues"], ["conflicts", "conflicts"]].map(([k, l]) => (
            <div key={k}><p className="font-mono-data text-2xl font-bold text-slate-900">{h[k]}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">{l}</p></div>
          ))}
        </div>
      </div>
      <div className="bg-slate-900 text-white p-5 flex flex-wrap items-center justify-between gap-3" data-testid="recommended-action">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/50">Recommended next action</p>
          <p className="text-sm font-bold mt-1">{d.recommended.message}</p>
        </div>
        {d.recommended.tab && (
          <button data-testid="start-review-btn" onClick={() => goTo(d.recommended.tab)}
            className="bg-white text-slate-900 text-xs font-bold px-4 py-2.5 hover:bg-slate-100 flex items-center gap-2">
            Start Review <ArrowRight size={13} weight="bold" />
          </button>
        )}
      </div>
      <div className="bg-white border border-slate-200 divide-y divide-slate-100" data-testid="action-queue">
        <p className="px-5 py-3 font-heading font-bold text-sm">Action Queue — exceptions requiring human intervention</p>
        {d.queue.map((q) => (
          <button key={q.id} data-testid={`queue-item-${q.id}`} onClick={() => goTo(q.tab)} disabled={!q.count}
            className="w-full px-5 py-3.5 flex items-center gap-4 text-left hover:bg-slate-50 disabled:opacity-60 disabled:cursor-default">
            {q.count
              ? <span className={`w-9 h-9 flex items-center justify-center text-white font-mono-data font-bold text-sm shrink-0 ${SEVERITY[q.severity]}`}>{q.count}</span>
              : <span className="w-9 h-9 flex items-center justify-center bg-emerald-50 text-emerald-700 shrink-0"><CheckCircle size={16} weight="bold" /></span>}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-slate-800">{q.label}</p>
              <p className="text-[11px] text-slate-500">{q.description}</p>
            </div>
            {q.count > 0 && <ArrowRight size={14} className="text-slate-300 shrink-0" />}
          </button>
        ))}
        <div className="px-5 py-3 flex items-center gap-2 text-[11px] text-slate-500" data-testid="release-status-row">
          <Warning size={12} className="text-slate-400" />
          Release status: <b className="uppercase text-slate-700">{(d.release_status || "draft").replaceAll("_", " ")}</b>
          <button onClick={() => goTo("release")} className="underline font-semibold ml-1">manage in Approval &amp; Release</button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400">All audit trails, reviewer records, version history and release controls are preserved — each tab is the full governance workspace for its exception type. Unverified evidence never silently becomes verified.</p>
    </div>
  );
};

export default function EvidenceReviewPage() {
  const { productId, product } = useProduct();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const goTo = (t) => setParams(t === "overview" ? {} : { tab: t });
  return (
    <PageShell pageId="evidence-review" overline={`Evidence · ${product?.name || ""}`} title="Evidence Review">
      <div className="flex flex-wrap gap-1" data-testid="review-tabs">
        {TABS.map(([t, l]) => (
          <button key={t} data-testid={`review-tab-${t}`} onClick={() => goTo(t)}
            className={`text-[11px] font-semibold px-3 py-1.5 ${tab === t ? "bg-slate-900 text-white" : "text-slate-500 border border-slate-200 hover:bg-slate-50 bg-white"}`}>{l}</button>
        ))}
      </div>
      {tab === "overview" && <Overview productId={productId} goTo={goTo} />}
      {tab === "duplicates" && <MergeReviewPage embedded />}
      {tab === "sources" && <CitationsPage embedded />}
      {tab === "conflicts" && <QualityPage embedded />}
      {tab === "validation" && <ValidationPage embedded />}
      {tab === "release" && <ReleasePage embedded />}
    </PageShell>
  );
}
